import { useEffect, useMemo, useState } from 'react';
import { apiFetch } from '@/lib/http';

const DEMO_STOREFRONT_PATH = /^\/api\/store-profiles\/\d+\/storefront-photo\/demo$/;

export function isAuthenticatedDemoStorefrontUrl(value: string | null | undefined): value is string {
  return typeof value === 'string' && DEMO_STOREFRONT_PATH.test(value);
}

interface ResolvedImageState {
  inputUrl: string | null;
  resolvedUrl: string | null;
  isLoading: boolean;
  hasError: boolean;
}

/** Resolve protected demo images through bearer auth; stored URLs pass through. */
export function useStorefrontImageSource(inputUrl: string | null | undefined) {
  const normalizedInput = inputUrl ?? null;
  const initialState = useMemo<ResolvedImageState>(() => ({
    inputUrl: normalizedInput,
    resolvedUrl: isAuthenticatedDemoStorefrontUrl(normalizedInput) ? null : normalizedInput,
    isLoading: isAuthenticatedDemoStorefrontUrl(normalizedInput),
    hasError: false,
  }), [normalizedInput]);
  const [state, setState] = useState<ResolvedImageState>(initialState);

  useEffect(() => {
    if (!isAuthenticatedDemoStorefrontUrl(normalizedInput)) {
      setState({ inputUrl: normalizedInput, resolvedUrl: normalizedInput, isLoading: false, hasError: false });
      return;
    }

    const controller = new AbortController();
    let objectUrl: string | null = null;
    setState({ inputUrl: normalizedInput, resolvedUrl: null, isLoading: true, hasError: false });

    void apiFetch(normalizedInput, { signal: controller.signal })
      .then(async (response) => {
        if (!response.ok) throw new Error(`Storefront image request failed (${response.status})`);
        const blob = await response.blob();
        if (!blob.type.startsWith('image/')) throw new Error('Storefront image response was invalid');
        objectUrl = URL.createObjectURL(blob);
        setState({ inputUrl: normalizedInput, resolvedUrl: objectUrl, isLoading: false, hasError: false });
      })
      .catch((error) => {
        if (controller.signal.aborted) return;
        console.warn('[STORE_PROFILES] Demo storefront image failed to load:', error);
        setState({ inputUrl: normalizedInput, resolvedUrl: null, isLoading: false, hasError: true });
      });

    return () => {
      controller.abort();
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [normalizedInput]);

  return state.inputUrl === normalizedInput ? state : initialState;
}
